import {
  getCountryDisplayName,
  getLanguageDisplayName,
} from "@/lib/utils/locale-display";
import type { Doc } from "@/convex/_generated/dataModel";

/**
 * Transforms a single country code to its display name
 * @param code - ISO country code (e.g., "US")
 * @returns Display name (e.g., "United States")
 */
export const transformCountryCode = (code: string): string => {
  return getCountryDisplayName(code) || code;
};

/**
 * Transforms a single language code to its display name
 * @param code - ISO language code (e.g., "en")
 * @returns Display name (e.g., "English")
 */
export const transformLanguageCode = (code: string): string => {
  return getLanguageDisplayName(code) || code;
};

/**
 * Transforms an array of country codes to their display names
 * @param codes - Array of ISO country codes
 * @returns Array of display names
 */
export const transformCountryCodes = (codes: string[]): string[] => {
  return codes.map(transformCountryCode);
};

/**
 * Transforms an array of language codes to their display names
 * @param codes - Array of ISO language codes
 * @returns Array of display names
 */
export const transformLanguageCodes = (codes: string[]): string[] => {
  return codes.map(transformLanguageCode);
};

/**
 * Creates a searchable text string from country codes
 * @param codes - Array of ISO country codes
 * @returns Space-separated string of country names
 */
export const createCountrySearchText = (codes: string[]): string => {
  return transformCountryCodes(codes).join(" ");
};

/**
 * Creates a searchable text string from language codes
 * @param codes - Array of ISO language codes
 * @returns Space-separated string of language names
 */
export const createLanguageSearchText = (codes: string[]): string => {
  return transformLanguageCodes(codes).join(" ");
};

/**
 * Creates a combined searchable text from both country and language codes
 * @param countryCodes - Array of ISO country codes
 * @param languageCodes - Array of ISO language codes
 * @returns Space-separated string of country and language names
 */
export const createLocationSearchText = (
  countryCodes: string[],
  languageCodes: string[]
): string => {
  const countries = createCountrySearchText(countryCodes);
  const languages = createLanguageSearchText(languageCodes);
  return `${countries} ${languages}`.trim();
};

/**
 * Transforms creator data for Meilisearch indexing
 * @param creator - Creator document from the database
 * @returns Transformed creator data with searchable text fields
 */
export const transformCreatorForSearch = (creator: Doc<"creators">) => {
  const countryCodes = creator.country || [];
  const languageCodes = creator.language || [];

  return {
    ...creator,
    _searchableText: {
      countries: createCountrySearchText(countryCodes),
      languages: createLanguageSearchText(languageCodes),
      location: createLocationSearchText(countryCodes, languageCodes),
    },
  };
};

/**
 * Transforms campaign data for Meilisearch indexing
 * @param campaign - Campaign document from the database
 * @returns Transformed campaign data with searchable text fields
 */
export const transformCampaignForSearch = (campaign: Doc<"campaigns">) => {
  // Handle country - convert to array if it's a string
  const countryCodes = campaign.country ? [campaign.country] : [];

  // Handle language - convert to array if it's a string
  const languageCodes = campaign.language ? [campaign.language] : [];

  // Handle social platforms
  const platforms = campaign.socialPlatforms || [];

  // Handle hashtags and key messages
  const hashtags = campaign.hashtags || [];
  const keyMessages = campaign.keyMessages || [];

  return {
    ...campaign,
    _searchableText: {
      countries: createCountrySearchText(countryCodes),
      languages: createLanguageSearchText(languageCodes),
      platforms: platforms.join(" "),
      hashtags: hashtags.join(" "),
      keyMessages: keyMessages.join(" "),
    },
  };
};
